<div class="edit-contact__header">
  <h2 mat-dialog-title class="edit-contact__title">Edit person contact</h2>
  <button type="button" class="edit-contact__close" mat-dialog-close aria-label="Close">
    <mat-icon>close</mat-icon>
  </button>
</div>
<mat-dialog-content class="edit-contact">
  @if (loading) {
    <div class="edit-contact__message">
      <mat-spinner diameter="32"></mat-spinner>
    </div>
  } @else if (hasError || !contact) {
    <p class="edit-contact__message">Could not load this contact. Please close and try again.</p>
  } @else {
    <div class="edit-contact__banner">
      <mat-icon class="edit-contact__banner-icon">link</mat-icon>
      <p>
        This contact is linked to a user account. All profile fields — including the mobile number and photo — stay in
        sync with the user, and the active status follows the user in Active Directory. You can only control what is
        shown to customers.
      </p>
    </div>

    <div class="edit-contact__fields">
      <div class="edit-contact__field">
        <span class="m-label">Name</span>
        <input class="m-input" disabled [value]="contact.displayName" />
      </div>
      <div class="edit-contact__field">
        <span class="m-label">Email</span>
        <input class="m-input" disabled [value]="contact.email" />
      </div>
      <div class="edit-contact__field">
        <span class="m-label">Business phone</span>
        <input class="m-input" disabled [value]="contact.phone || '--'" />
      </div>
      <div class="edit-contact__field">
        <span class="m-label">Mobile phone</span>
        <input class="m-input" disabled [value]="contact.mobilePhone || '--'" />
      </div>
      <div class="edit-contact__field">
        <span class="m-label">Job title</span>
        <input class="m-input" disabled [value]="contact.jobTitle || '--'" />
      </div>
      <div class="edit-contact__field" style="grid-column: 1 / -1">
        <span class="m-label">Location</span>
        <input class="m-input" disabled [value]="contact.address || '--'" />
      </div>
    </div>

    <label class="edit-contact__check">
      <input type="checkbox" [checked]="contact.isActive" disabled />
      <span class="edit-contact__active-label">Active</span>
      <span class="edit-contact__hint"
        >Synced from Active Directory — a contact is active only while the user account is active there.</span
      >
    </label>

    <div class="edit-contact__card" [formGroup]="editForm">
      <p class="m-label">Customer visibility — default</p>
      <p class="edit-contact__card-description">
        Both are hidden from customers unless switched on. These are the defaults — they can be changed per organisation
        at organisation level.
      </p>

      <div class="edit-contact__visibility-list">
        <label class="edit-contact__visibility-row">
          <span class="edit-contact__visibility-icon">
            <mat-icon>smartphone</mat-icon>
          </span>
          <span class="edit-contact__visibility-text">
            <span class="edit-contact__visibility-title">Show <strong>mobile phone</strong> to customers</span>
            <span class="edit-contact__visibility-subtitle">Default &middot; shown alongside the office number</span>
          </span>
          <input type="checkbox" formControlName="showMobilePhone" />
        </label>

        <label class="edit-contact__visibility-row">
          <app-avatar
            [size]="36"
            [fontSizeOverride]="12"
            [displayName]="contact.displayName"
            [photoUrl]="data.photoUrl"></app-avatar>
          <span class="edit-contact__visibility-text">
            <span class="edit-contact__visibility-title">Show <strong>photo</strong> to customers</span>
            <span class="edit-contact__visibility-subtitle">Default &middot; photo synced from the user's profile</span>
          </span>
          <input type="checkbox" formControlName="showPhoto" />
        </label>
      </div>
    </div>

    <div class="edit-contact__assign-head">
      <span class="m-label edit-contact__assign-title"
        >Customer assignment
        <span class="edit-contact__assign-subtitle">— add a role label per organisation (optional)</span></span
      >
      <span class="edit-contact__count">{{ editForm.controls.assignments.value.length }} assigned</span>
      @if (editForm.controls.assignments.value.length) {
        <button type="button" class="edit-contact__clear" (click)="clearAll()">Clear all</button>
      }
    </div>

    @if (editForm.controls.assignments.value.length) {
      <div class="edit-contact__assignments">
        @for (assignment of editForm.controls.assignments.value; track assignment.partyId) {
          <div class="edit-contact__assignment-row">
            <mat-icon class="edit-contact__assignment-icon">apartment</mat-icon>
            <span class="edit-contact__chip-text">
              <span class="edit-contact__chip-name">{{ assignment.partyName }}</span>
              <span class="edit-contact__chip-number">{{ assignment.partyNumber }}</span>
            </span>
            <input
              class="edit-contact__assignment-role"
              placeholder="Role for this customer (optional)"
              [value]="assignment.role ?? ''"
              (change)="updateRole(assignment.partyId, $any($event.target).value)" />
            <button
              type="button"
              class="edit-contact__chip-x"
              [attr.aria-label]="'Remove ' + assignment.partyName"
              (click)="remove(assignment)">
              &times;
            </button>
          </div>
        }
      </div>
    } @else {
      <p class="edit-contact__hint">No parties assigned yet — search below to add.</p>
    }

    <div class="edit-contact__search">
      <mat-icon class="edit-contact__search-icon">search</mat-icon>
      <input
        class="edit-contact__search-input"
        type="text"
        role="combobox"
        autocomplete="off"
        aria-autocomplete="list"
        aria-controls="edit-contact-party-list"
        [attr.aria-activedescendant]="partyHighlight >= 0 ? 'edit-contact-party-opt-' + partyHighlight : null"
        placeholder="Search parties by name or customer number..."
        aria-label="Search parties by name or customer number"
        [formControl]="partyControl"
        (keydown)="onPartyKeydown($event)" />
    </div>

    @if (partySearchState$ | async; as state) {
      @if (state.loading || state.error || state.searched) {
        <div class="edit-contact__sugg-panel" id="edit-contact-party-list" role="listbox" aria-label="Parties">
          @if (state.loading) {
            <div class="edit-contact__sugg edit-contact__sugg--message" role="presentation">
              <mat-spinner diameter="16" /><span>Searching...</span>
            </div>
          } @else if (state.error) {
            <div class="edit-contact__sugg edit-contact__sugg--message" role="presentation">Unable to load parties</div>
          } @else {
            @for (party of state.parties; track party.id; let i = $index) {
              <button
                type="button"
                role="option"
                [id]="'edit-contact-party-opt-' + i"
                class="edit-contact__sugg"
                [class.edit-contact__sugg--active]="i === partyHighlight"
                [attr.aria-selected]="i === partyHighlight"
                (click)="addAssignment(party)">
                <span class="edit-contact__chip-name">{{ party.name }}</span>
                <span class="edit-contact__chip-number">{{ party.number }}</span>
              </button>
            } @empty {
              <div class="edit-contact__sugg edit-contact__sugg--message" role="presentation">No parties found</div>
            }
          }
        </div>
      }
    }
  }
</mat-dialog-content>
<mat-dialog-actions class="edit-contact__actions">
  <button type="button" class="m-btn m-btn-secondary" mat-dialog-close>Cancel</button>
  <button
    type="button"
    class="m-btn m-btn-primary"
    [disabled]="loading || saving || hasError || !contact"
    (click)="save()">
    {{ saving ? 'Saving...' : 'Save changes' }}
    <span class="sgl-arrows" aria-hidden="true"></span>
  </button>
</mat-dialog-actions>
